// third-party
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// project imports
import axios from 'utils/axios';

// types
import { ApiResponse, DefaultRootStateProps } from 'types';
import getCategoriesServiceMock from './mocks/getCategoriesServiceMock';
import getFacetsServiceMock from './mocks/getFacetsServiceMock';
import {
    getFacetsServiceProps,
    uploadMassiveFileProps,
    editAdditionalFieldProps,
    editFacetServiceProps,
    downloadMassiveFileProps,
    aditionalFieldsProps,
    downloadMassiveImagesProps
} from 'types/slices/slices';
import { BrandType, VendorType } from 'types/components/shared';
import { editBrandService, getBrandsService, getRolesService, getTotalVendorsService } from 'services/catalogs';
import { createBrandService } from '../../services/catalogs';
import { CreateBrandProps, CreateBrandResponseType } from '../../services/catalog.types';
import { getVendorsService } from 'services/user';

const initialState: DefaultRootStateProps['catalogue'] = {
    loading: true,
    error: null,
    brands: [],
    facets: [],
    vendors: [],
    categories: [],
    images: [],
    totalVendors: [],
    roles: {
        profile: [],
        approval: []
    }
};

const slice = createSlice({
    name: 'catalog',
    initialState,
    reducers: {
        // HAS ERROR
        hasError(state, action) {
            state.error = action.payload;
        }
    },
    extraReducers(builder) {
        // VENDORS

        // VENDORS
        builder
            .addCase(getVendors.pending, (state) => {
                state.loading = true;
            })
            .addCase(getVendors.fulfilled, (state, action) => {
                const vendors: VendorType[] = action.payload;

                vendors.push({ countryId: '', idProvider: 0, name: 'No vendor' });

                state.loading = false;
                state.vendors = vendors;
            });
        builder
            .addCase(getRoles.pending, (state) => {
                state.loading = true;
            })
            .addCase(getRoles.fulfilled, (state, action) => {
                state.loading = false;

                state.roles = action.payload;
            });
        builder
            .addCase(getTotalVendors.pending, (state) => {
                state.loading = true;
            })
            .addCase(getTotalVendors.fulfilled as any, (state, action) => {
                state.loading = false;

                state.totalVendors = action.payload;
            });
        // FACETS
        builder
            .addCase(getFacets.pending, (state) => {
                state.loading = true;
            })
            .addCase(getFacets.fulfilled, (state, action) => {
                state.loading = false;
                state.facets = action.payload;
            });
        builder
            .addCase(getBrands.pending, (state) => {
                state.loading = true;
            })
            .addCase(getBrands.fulfilled, (state, action) => {
                state.loading = false;
                state.brands = action.payload;
            });
        // BRANDS
        builder
            .addCase(createBrand.pending, (state) => {
                state.loading = true;
            })
            .addCase(createBrand.fulfilled, (state, action) => {
                const response = action.payload.response as CreateBrandResponseType[];

                let newBrans = [...state.brands];

                state.brands = newBrans.map((item) => {
                    const itemResp = response.find((itemResp) => itemResp.idMerchant === item.merchantId);

                    if (itemResp) {
                        return { ...item, brands: [...item.brands, itemResp.brandData] };
                    }

                    return item;
                });

                state.loading = false;
            });

        // CATEGORIES
        builder
            .addCase(getCategories.pending, (state) => {
                state.loading = true;
            })
            .addCase(getCategories.fulfilled, (state, action) => {
                const categories = action.payload.response;
                state.loading = false;
                if (categories) {
                    state.categories = categories;
                }
            });
    }
});

export default slice.reducer;

/* ============ BRANDS ============ */

export const getBrands = createAsyncThunk(`${slice.name}/getBrands`, async ({ idMerchant }: { idMerchant?: number | string }) => {
    const { data } = await getBrandsService({ idMerchant });

    return data.response;
});

export const editBrand = createAsyncThunk(
    `${slice.name}/editBrand`,
    async ({ dataBrand, idMerchant }: { dataBrand: BrandType; idMerchant?: number }) => {
        if (!idMerchant) throw new Error('idMerchant is required');

        const response = await editBrandService({ dataBrand, idMerchant });
        return response.data;
    }
);

export const createBrand = createAsyncThunk(`${slice.name}/createBrand`, async (dataBrand: CreateBrandProps | CreateBrandProps[]) => {
    const { data } = await createBrandService(dataBrand);

    return data as ApiResponse<CreateBrandResponseType[]>;
});

/* ============ VENDORS ============ */

export const getTotalVendors = createAsyncThunk(
    `${slice.name}/getTotalVendors`,
    async ({ merchantId, statusId, email }: { merchantId?: number | string; statusId?: number | string; email?: string }) => {
        const { data } = await getTotalVendorsService({ merchantId, statusId, email });

        return data;
    }
);

export const getRoles = createAsyncThunk(`${slice.name}/getRoles`, async () => {
    const { data } = await getRolesService();

    return data;
});

// TODO: update the services from this

/* ============ FACETS ============ */

export const getFacets = createAsyncThunk(`${slice.name}/getFacets`, async ({ term, page, idMerchant }: getFacetsServiceProps) => {
    let result: any;
    let facetsPag: any;
    if (idMerchant) {
        try {
            const response = await axios.get(`/loolu/api/facets/raw-multicatalog/merchant/${idMerchant}/search`, {
                params: {
                    searchParam: term,
                    pageNum: page
                }
            });
            result = response.data;
        } catch (error) {
            result = getFacetsServiceMock;
        }
        facetsPag = result?.response?.map((item: any) => ({ ...item, totalPages: result?.totalPages }));
    }
    return facetsPag;
});

export const editFacet = createAsyncThunk(`${slice.name}/editFacet`, async ({ idMerchant, data }: editFacetServiceProps) => {
    try {
        const response = await axios.put(`/loolu/api/facets/raw/merchant/${idMerchant}`, [data], {});
        return response.data;
    } catch (e: any) {
        return e.response;
    }
});

/* ============ CATEGORIES ============ */

export const getCategories = createAsyncThunk(`${slice.name}/getMerchantCategories`, async (idMerchant: number | string) => {
    let result;
    try {
        const response = await axios.get(`/loolu/api/category/search-multicatalog`, {
            params: {
                idMerchant // TODO: QUITAR este parametro
            }
        });
        result = response.data;
    } catch (error) {
        result = getCategoriesServiceMock;
    }
    return result;
});

// MASSIVE LOAD

export const uploadMassiveFile = createAsyncThunk(
    `${slice.name}/uploadMassiveFile`,
    async ({ idMerchant, file, user }: uploadMassiveFileProps) => {
        try {
            const data = new FormData();
            data.append('file', file);

            const response = await axios.post(`/loolu/api/bulkload/merchant/${idMerchant}/user/${user}/upload/file`, data, {
                /*baseURL: STYRK_API_BULKLOAD*/
            });
            return response.data;
        } catch (e: any) {
            return e.response;
        }
    }
);

export const downloadMassiveImages = createAsyncThunk(
    `${slice.name}/images`,
    async ({ idMerchant, idCategory, idBrand, vendorId }: downloadMassiveImagesProps) => {
        try {
            const response = await axios.get(
                `/loolu/api/catalog/images?merchantId=${idMerchant}&categoryId=${idCategory}&brandId=${idBrand}`
            );
            return response.data;
        } catch (e: any) {
            return e.response;
        }
    }
);

export const downloadMassiveFile = createAsyncThunk(
    `${slice.name}/uploadMassiveFile`,
    async ({ idMerchant, user, token, productName, idSKU, idCategory, idBrand, eans }: downloadMassiveFileProps) => {
        try {
            const response = await axios.post(
                `/loolu/api/bulkload/merchant/${idMerchant}/user/${user}/contentRequest`,
                {
                    bearerToken: token,
                    idMerchant,
                    user,
                    productName,
                    idSKU,
                    idCategory,
                    idBrand,
                    eans
                },
                {
                    /*baseURL: STYRK_API_BULKLOAD*/
                }
            );
            return response.data;
        } catch (e: any) {
            return e.response;
        }
    }
);
export const getAllAdditionalFields = createAsyncThunk(
    `${slice.name}/getAdditionalFields`,
    async ({ idMerchant }: { idMerchant: number }) => {
        try {
            const response = await axios.get(`/loolu/api/additionalFields/list`, {
                params: {
                    merchantId: idMerchant,
                    idMerchant
                }
            });
            return response.data;
        } catch (e: any) {
            return e.response;
        }
    }
);

export const getAdditionalFields = createAsyncThunk(
    `${slice.name}/getAdditionalFields`,
    async ({ idMerchant, productId }: { idMerchant: number; productId: number | string }) => {
        try {
            const response = await axios.get(`/loolu/api/product/detail/productSkus`, {
                params: {
                    merchantId: idMerchant,
                    idProd: productId
                }
            });
            return response.data;
        } catch (e: any) {
            return e.response;
        }
    }
);

export const getImage = createAsyncThunk(`${slice.name}/getImage`, async (url: string) => {
    try {
        const response = await axios.get('/loolu/api/catalog/images/getImage', {
            params: { imageUrl: url },
            responseType: 'blob'
        });

        let filename = (url.split('/').pop() || 'default_name.jpg').split('?')[0];

        if (filename.length > 85) {
            filename = filename.substring(filename.length - 85);
        }
        const imageFile = new File([response.data], filename === 'null' ? 'default.jpg' : filename, { type: response.data.type });
        return imageFile;
    } catch (e: any) {
        return e.response;
    }
});

export const createMultipleAdditionalFields = createAsyncThunk(
    `${slice.name}/createMultipleAdditionalFields`,
    async ({ additionalFields }: { additionalFields: aditionalFieldsProps[] }) => {
        try {
            const requests = additionalFields.map((data) => axios.post(`/loolu/api/additionalFields/product`, data, {}));

            const responses = await Promise.allSettled(requests);

            const responseData = responses.map((response) => {
                if (response.status === 'rejected') {
                    const data = JSON.parse(response.reason.config.data);
                    return { ...response.reason.response.data, merchantId: data.merchantId };
                }

                return response.value.data.response;
            });

            return responseData;
        } catch (e: any) {
            return e.response;
        }
    }
);

export const editAdditionalField = createAsyncThunk(`${slice.name}/createAdditionalField`, async ({ data }: editAdditionalFieldProps) => {
    try {
        const response = await axios.put(`/loolu/api/additionalFields/product`, data, {});
        return response.data;
    } catch (e: any) {
        return e.response;
    }
});

export const deleteAdditionalField = createAsyncThunk(`${slice.name}/deleteAdditionalField`, async (fieldId: number) => {
    try {
        const response = await axios.delete(`/loolu/api/additionalFields/product/${fieldId}`, {});
        return response.data;
    } catch (e: any) {
        return e.response;
    }
});

export const getVendors = createAsyncThunk(
    `${slice.name}/getVendors`,
    async ({ idMerchant }: { idMerchant?: number; idProvider?: number }, { dispatch }) => {
        try {
            const response = await getVendorsService(idMerchant);

            return response.data.response;
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            throw error;
        }
    }
);
